import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  RemoveCircleOutline as SkippedIcon,
} from '@mui/icons-material';
import {
  pollBackfillPreview,
  reportFeedIssue,
  startBackfillPreview,
} from '../../api/contentCompletion';
import {
  BackfillFeedType,
  BackfillPreviewResult,
  CompanyContentHealth,
} from '../../types/contentCompletion';
import BatchBackfillReviewDialog from './BatchBackfillReviewDialog';
import { resolveDefaultSourceUrl, resolveSourceCandidates } from './backfillUtils';

type ItemStatus =
  | 'queued'
  | 'previewing'
  | 'preview_ready'
  | 'preview_failed'
  | 'no_results'
  | 'no_feed'
  | 'ingested'
  | 'error';

interface BatchItem {
  companyId: string;
  companyName: string;
  company: CompanyContentHealth;
  sourceUrl: string;
  status: ItemStatus;
  previewResult?: BackfillPreviewResult;
  error?: string;
}

interface BatchBackfillPanelProps {
  companies: CompanyContentHealth[];
  onClose: () => void;
  onComplete: () => void;
  onIssueReported: () => void;
}

const DAYS_BACK_MIN = 1;
const DAYS_BACK_MAX = 730;
const DAYS_BACK_DEFAULT = 30;

const clampDaysBack = (value: number) =>
  Math.min(DAYS_BACK_MAX, Math.max(DAYS_BACK_MIN, value));

const STATUS_META: Record<
  ItemStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  queued: { label: 'Queued', color: 'default' },
  previewing: { label: 'Previewing…', color: 'info' },
  preview_ready: { label: 'Ready to review', color: 'warning' },
  preview_failed: { label: 'Unreachable', color: 'error' },
  no_results: { label: 'No results', color: 'warning' },
  no_feed: { label: 'No feed URL', color: 'default' },
  ingested: { label: 'Saved', color: 'success' },
  error: { label: 'Error', color: 'error' },
};

const RERUN_STATUSES = new Set<ItemStatus>([
  'queued',
  'preview_failed',
  'no_results',
  'error',
]);

const buildInitialItems = (companies: CompanyContentHealth[]): BatchItem[] =>
  companies.map((company) => ({
    companyId: company.company_id,
    companyName: company.company_name,
    company,
    sourceUrl: resolveDefaultSourceUrl(company),
    status: 'queued' as ItemStatus,
  }));

const BatchBackfillPanel: React.FC<BatchBackfillPanelProps> = ({
  companies,
  onClose,
  onComplete,
  onIssueReported,
}) => {
  const [items, setItems] = useState<BatchItem[]>(() => buildInitialItems(companies));
  const [running, setRunning] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [daysBack, setDaysBack] = useState(DAYS_BACK_DEFAULT);
  const [feedType, setFeedType] = useState<BackfillFeedType>('auto');
  const cancelledRef = useRef(false);
  const runIdRef = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const updateItem = useCallback((companyId: string, patch: Partial<BatchItem>) => {
    setItems((prev) =>
      prev.map((item) => (item.companyId === companyId ? { ...item, ...patch } : item))
    );
  }, []);

  const reportUnreachable = useCallback(
    async (
      item: BatchItem,
      issueType: 'fetch_failed' | 'zero_results',
      note: string | undefined,
      daysBackUsed: number
    ) => {
      if (!item.sourceUrl.trim()) return;
      try {
        await reportFeedIssue({
          company_id: item.companyId,
          company_name: item.companyName,
          source_url: item.sourceUrl.trim(),
          issue_type: issueType,
          days_back: daysBackUsed,
          note,
        });
        onIssueReported();
      } catch {
        /* batch continues; user can report manually from drawer */
      }
    },
    [onIssueReported]
  );

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      runIdRef.current += 1;
    };
  }, []);

  const startPreviews = useCallback(
    async (options?: { rerunOnly?: boolean }) => {
      const daysBackUsed = clampDaysBack(daysBack);
      const feedTypeUsed = feedType;
      setDaysBack(daysBackUsed);

      const runId = runIdRef.current + 1;
      runIdRef.current = runId;
      cancelledRef.current = false;
      setHasStarted(true);
      setRunning(true);
      setReviewOpen(false);

      const currentItems = itemsRef.current;
      const targets = options?.rerunOnly
        ? companies.filter((company) => {
            const existing = currentItems.find((item) => item.companyId === company.company_id);
            return !existing || RERUN_STATUSES.has(existing.status);
          })
        : companies;

      setItems((prev) => {
        const byId = new Map(prev.map((item) => [item.companyId, item]));
        return companies.map((company) => {
          const existing = byId.get(company.company_id);
          const shouldReset =
            !options?.rerunOnly || !existing || RERUN_STATUSES.has(existing.status);
          if (!shouldReset && existing) {
            return existing;
          }
          return {
            companyId: company.company_id,
            companyName: company.company_name,
            company,
            sourceUrl: resolveDefaultSourceUrl(company),
            status: 'queued' as ItemStatus,
          };
        });
      });

      for (const seed of targets) {
        if (cancelledRef.current || runIdRef.current !== runId) break;

        const candidates = resolveSourceCandidates(seed);
        if (candidates.length === 0) {
          updateItem(seed.company_id, { status: 'no_feed', sourceUrl: '' });
          continue;
        }

        updateItem(seed.company_id, {
          status: 'previewing',
          sourceUrl: candidates[0],
          previewResult: undefined,
          error: undefined,
        });

        let settled = false;
        let lastFailure: {
          sourceUrl: string;
          previewResult?: BackfillPreviewResult;
          error?: string;
          issueType: 'fetch_failed' | 'zero_results';
        } | null = null;

        for (let i = 0; i < candidates.length; i += 1) {
          if (cancelledRef.current || runIdRef.current !== runId) break;

          const sourceUrl = candidates[i];
          const isLastCandidate = i === candidates.length - 1;
          updateItem(seed.company_id, { status: 'previewing', sourceUrl });

          try {
            const { run_id } = await startBackfillPreview({
              company_id: seed.company_id,
              company_name: seed.company_name,
              source_url: sourceUrl,
              feed_type: feedTypeUsed,
              days_back: daysBackUsed,
              fetch_content: true,
            });

            const previewResult = await new Promise<BackfillPreviewResult>((resolve) => {
              const stop = pollBackfillPreview(run_id, (update) => {
                if (update.status !== 'running') {
                  stop();
                  resolve(update);
                }
              });
            });

            if (cancelledRef.current || runIdRef.current !== runId) break;

            const batchItem: BatchItem = {
              companyId: seed.company_id,
              companyName: seed.company_name,
              company: seed,
              sourceUrl,
              status: 'preview_ready',
              previewResult,
            };

            if (previewResult.status === 'failure') {
              lastFailure = {
                sourceUrl,
                previewResult,
                error: previewResult.error || 'Preview failed',
                issueType: 'fetch_failed',
              };
              if (!isLastCandidate) continue;
              await reportUnreachable(
                batchItem,
                'fetch_failed',
                previewResult.error,
                daysBackUsed
              );
              updateItem(seed.company_id, {
                status: 'preview_failed',
                sourceUrl,
                previewResult,
                error: previewResult.error || 'Preview failed',
              });
              settled = true;
              break;
            }

            if (previewResult.press_releases_found === 0) {
              lastFailure = {
                sourceUrl,
                previewResult,
                issueType: 'zero_results',
              };
              if (!isLastCandidate) continue;
              await reportUnreachable(
                batchItem,
                'zero_results',
                previewResult.error,
                daysBackUsed
              );
              updateItem(seed.company_id, {
                status: 'no_results',
                sourceUrl,
                previewResult,
              });
              settled = true;
              break;
            }

            updateItem(seed.company_id, {
              status: 'preview_ready',
              sourceUrl,
              previewResult,
              error: undefined,
            });
            settled = true;
            break;
          } catch (err: unknown) {
            if (cancelledRef.current || runIdRef.current !== runId) break;
            const msg =
              (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
              (err as Error)?.message ||
              'Preview failed';
            lastFailure = { sourceUrl, error: msg, issueType: 'fetch_failed' };
            if (!isLastCandidate) continue;
            const batchItem: BatchItem = {
              companyId: seed.company_id,
              companyName: seed.company_name,
              company: seed,
              sourceUrl,
              status: 'error',
            };
            await reportUnreachable(batchItem, 'fetch_failed', msg, daysBackUsed);
            updateItem(seed.company_id, {
              status: 'preview_failed',
              sourceUrl,
              error: msg,
            });
            settled = true;
            break;
          }
        }

        if (
          !settled &&
          lastFailure &&
          !cancelledRef.current &&
          runIdRef.current === runId
        ) {
          updateItem(seed.company_id, {
            status:
              lastFailure.issueType === 'zero_results' ? 'no_results' : 'preview_failed',
            sourceUrl: lastFailure.sourceUrl,
            previewResult: lastFailure.previewResult,
            error: lastFailure.error,
          });
        }
      }

      if (runIdRef.current === runId) {
        setRunning(false);
      }
    },
    [companies, daysBack, feedType, reportUnreachable, updateItem]
  );

  const handleCompanyIngested = (companyId: string) => {
    updateItem(companyId, { status: 'ingested' });
    onComplete();
  };

  const handleDismiss = () => {
    cancelledRef.current = true;
    runIdRef.current += 1;
    setRunning(false);
    onClose();
  };

  const reviewItems = items
    .filter((item) => item.status === 'preview_ready' && item.previewResult)
    .map((item) => ({
      companyId: item.companyId,
      companyName: item.companyName,
      company: item.company,
      sourceUrl: item.sourceUrl,
      previewResult: item.previewResult as BackfillPreviewResult,
    }));

  const total = items.length;
  const finishedCount = items.filter(
    (item) => item.status !== 'queued' && item.status !== 'previewing'
  ).length;
  const processing = items.find((item) => item.status === 'previewing');
  const readyCount = items.filter((item) => item.status === 'preview_ready').length;
  const ingestedCount = items.filter((item) => item.status === 'ingested').length;
  const unreachableCount = items.filter(
    (item) => item.status === 'preview_failed' || item.status === 'no_results'
  ).length;
  const rerunCount = items.filter((item) => RERUN_STATUSES.has(item.status)).length;
  const settingsLocked = running;
  const allDone =
    hasStarted &&
    !running &&
    items.every(
      (item) =>
        item.status === 'ingested' ||
        item.status === 'preview_failed' ||
        item.status === 'no_results' ||
        item.status === 'no_feed' ||
        item.status === 'error' ||
        item.status === 'preview_ready'
    );

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'secondary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PlayArrowIcon color="secondary" />
          <Typography variant="h6" fontWeight={700}>
            Batch backfill — {total} compan{total === 1 ? 'y' : 'ies'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={handleDismiss}>
            {allDone && !running ? 'Done' : 'Dismiss'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={settingsLocked}>
            <InputLabel>Feed type</InputLabel>
            <Select
              value={feedType}
              label="Feed type"
              onChange={(e) => setFeedType(e.target.value as BackfillFeedType)}
            >
              <MenuItem value="auto">Auto-detect</MenuItem>
              <MenuItem value="rss">RSS</MenuItem>
              <MenuItem value="html">HTML page</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Days back"
            type="number"
            size="small"
            value={daysBack}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                setDaysBack(DAYS_BACK_DEFAULT);
                return;
              }
              const parsed = parseInt(raw, 10);
              if (Number.isNaN(parsed)) {
                setDaysBack(DAYS_BACK_DEFAULT);
                return;
              }
              setDaysBack(clampDaysBack(parsed));
            }}
            inputProps={{ min: DAYS_BACK_MIN, max: DAYS_BACK_MAX }}
            sx={{ width: 140 }}
            disabled={settingsLocked}
            helperText={!hasStarted ? 'How far back to search for PRs' : undefined}
          />
          {!hasStarted ? (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PlayArrowIcon />}
              onClick={() => startPreviews()}
              disabled={running || total === 0}
            >
              Start previews
            </Button>
          ) : (
            !running &&
            rerunCount > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => startPreviews({ rerunOnly: true })}
              >
                Re-run empty/failed ({rerunCount}) with {daysBack}d
              </Button>
            )
          )}
        </Box>

        {!hasStarted && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Set feed type and days back, then start. A wider window (e.g. 180) finds older PRs that
            a 30-day search would miss.
          </Alert>
        )}

        {hasStarted && (
          <LinearProgress
            variant="determinate"
            value={total === 0 ? 0 : (finishedCount / total) * 100}
            sx={{ mb: 1, borderRadius: 1 }}
          />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {!hasStarted ? (
            'Waiting to start…'
          ) : running ? (
            processing ? (
              <>
                Previewing {finishedCount + 1}/{total}: <strong>{processing.companyName}</strong>
                {' · '}
                {daysBack} days · {feedType}
              </>
            ) : (
              'Starting previews…'
            )
          ) : (
            <>
              Previews complete ({daysBack} days, {feedType}).
              {readyCount > 0 ? ` ${readyCount} ready to review and save.` : ''}
              {ingestedCount > 0
                ? ` ${ingestedCount} compan${ingestedCount === 1 ? 'y' : 'ies'} saved this session.`
                : ''}
              {unreachableCount > 0
                ? ` ${unreachableCount} added to unreachable feed list.`
                : ''}
            </>
          )}
        </Typography>

        {readyCount > 0 && !running && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Review press releases for all ready companies and save selected items to Neo4j.
          </Alert>
        )}

        {readyCount > 0 && !running && (
          <Box sx={{ mb: 1 }}>
            <Button variant="contained" color="secondary" onClick={() => setReviewOpen(true)}>
              Review all ({readyCount})
            </Button>
          </Box>
        )}

        {unreachableCount > 0 && !running && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Failed or empty feeds were automatically added to the unreachable feed URLs list. You
            can widen Days back and re-run empty/failed companies.
          </Alert>
        )}

        <Divider sx={{ my: 1 }} />

        <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
          {items.map((item) => {
            const meta = STATUS_META[item.status];
            return (
              <ListItem key={item.companyId}>
                <Box
                  sx={{
                    mr: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    width: 24,
                    justifyContent: 'center',
                  }}
                >
                  {item.status === 'previewing' && <CircularProgress size={18} />}
                  {item.status === 'ingested' && (
                    <CheckCircleIcon color="success" fontSize="small" />
                  )}
                  {item.status === 'no_feed' && <SkippedIcon color="disabled" fontSize="small" />}
                  {(item.status === 'preview_failed' ||
                    item.status === 'no_results' ||
                    item.status === 'error') && <ErrorIcon color="error" fontSize="small" />}
                </Box>
                <ListItemText
                  primary={item.companyName}
                  secondary={
                    item.error ||
                    (item.status === 'no_feed'
                      ? 'No press release or RSS URL configured'
                      : item.status === 'no_results' &&
                          item.previewResult?.latest_press_release_date
                        ? `No PRs in last ${item.previewResult.days_back} days · latest ${item.previewResult.latest_press_release_date}`
                        : item.sourceUrl || undefined)
                  }
                  secondaryTypographyProps={{
                    color: item.error ? 'error' : 'textSecondary',
                    noWrap: true,
                  }}
                />
                <Chip
                  label={meta.label}
                  color={meta.color}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
              </ListItem>
            );
          })}
        </List>
      </Paper>

      {reviewOpen && reviewItems.length > 0 && (
        <BatchBackfillReviewDialog
          open
          onClose={() => setReviewOpen(false)}
          items={reviewItems}
          daysBack={daysBack}
          feedType={feedType}
          onCompanyIngested={handleCompanyIngested}
          onComplete={onComplete}
        />
      )}
    </>
  );
};

export default BatchBackfillPanel;
